// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AppIcon, Button, Modal, Spinner, TextArea, ViewLoAModal, useToast } from '@loa/ui-components';
import { clientListApi } from '../api/clientListApi';
import { amlApi } from '../api/amlApi';
import { clientDocumentApi } from '../api/clientDocumentApi';
import type { ClientDetailDTO } from '../types/clientList';
import type { AmlSummaryEntryDTO } from '../types/aml';
import { RequestAmlDocumentsModal } from '../components/aml/RequestAmlDocumentsModal';
import { RequestDisplayStatusBadge } from '../components/requests/RequestDisplayStatusBadge';
import type { RequestOverviewDisplayStatus } from '../types/requestOverview';
import { LOATableCard, EyeIcon, DownloadIcon, BanIcon } from '../components/loa';
import type { LOATableColumn, LOATableAction } from '../components/loa';
import { usePageHeader } from '../contexts/PageHeaderContext';

/**
 * Per-client AML Documentation page — ONE standard table card (the shared
 * LOATableCard layout used by the Requests / Documents tabs): the latest
 * state per requirement with inline View / Download / Approve / Decline
 * row actions, plus the "Request Documents" entry point in the header.
 * Reached from the ClientList AML button and the relationship Requests
 * tab rows.
 *
 * See docs/specs/aml-documentation-request-prd.md §10.2/§10.3.
 */

const ITEM_STATUS_TO_DISPLAY: Record<AmlSummaryEntryDTO['status'], RequestOverviewDisplayStatus> = {
  REQUESTED: 'PENDING_CLIENT',
  UPLOADED: 'PENDING_ADVISOR',
  APPROVED: 'COMPLETE',
  REJECTED: 'REJECTED',
  CANCELLED: 'VOIDED',
};

export function ClientAmlPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();
  const { showToast } = useToast();

  const [client, setClient] = useState<ClientDetailDTO | null>(null);
  const [summary, setSummary] = useState<AmlSummaryEntryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<AmlSummaryEntryDTO | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [viewState, setViewState] = useState<{ title: string } | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [viewContentType, setViewContentType] = useState<string | undefined>(undefined);
  const [viewLoading, setViewLoading] = useState(false);

  const load = useCallback(async (detail: ClientDetailDTO): Promise<void> => {
    const entries = await amlApi.summary(detail.profileId);
    setSummary(entries.filter((e) => e.clientId === detail.id));
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!client) return;
    try {
      await load(client);
    } catch {
      // Non-fatal: the page keeps its last state; the next full load reports.
    }
  }, [client, load]);

  useEffect(() => {
    let cancelled = false;
    const init = async (): Promise<void> => {
      if (!clientId) return;
      setLoading(true);
      setError(null);
      try {
        const detail = await clientListApi.getClientDetail(clientId);
        if (cancelled) return;
        setClient(detail);
        await load(detail);
      } catch (err: unknown) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setFeatureDisabled(true);
        } else {
          console.error('Failed to load AML documentation:', err);
          setError('The AML documentation could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [clientId, load]);

  useEffect(() => {
    if (client) {
      setBreadcrumbItems([
        { label: 'Relationships', to: '/relationships' },
        { label: `${client.firstName} ${client.lastName}`, to: `/clients/${client.id}` },
        { label: 'AML Documentation' },
      ]);
      setBackTo(`/clients/${client.id}`);
    }
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [client, setBreadcrumbItems, setBackTo]);

  const clientName = useMemo(
    () => (client ? `${client.firstName} ${client.lastName}` : ''),
    [client],
  );

  const applyDecisionResult = useCallback(
    (renewalRequestId?: string | null): void => {
      if (renewalRequestId) {
        showToast(
          <>
            <strong>Review complete.</strong> A new request for the declined documents was sent to the client.
          </>,
          'success',
        );
      }
      void refresh();
    },
    [refresh, showToast],
  );

  const handleApprove = useCallback(
    async (entry: AmlSummaryEntryDTO): Promise<void> => {
      setBusyItemId(entry.itemId);
      try {
        const result = await amlApi.approve(entry.requestId, entry.itemId);
        showToast(<><strong>{entry.requirementLabel}</strong> approved.</>, 'success');
        applyDecisionResult(result.renewalRequestId);
      } catch (e: unknown) {
        const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        showToast(<>{message ?? 'Failed to record the decision.'}</>, 'error');
      } finally {
        setBusyItemId(null);
      }
    },
    [applyDecisionResult, showToast],
  );

  const handleRejectConfirm = useCallback(async (): Promise<void> => {
    if (!rejecting || !rejectReason.trim()) return;
    const entry = rejecting;
    setBusyItemId(entry.itemId);
    try {
      const result = await amlApi.reject(entry.requestId, entry.itemId, rejectReason.trim());
      showToast(<><strong>{entry.requirementLabel}</strong> declined.</>, 'success');
      setRejecting(null);
      setRejectReason('');
      applyDecisionResult(result.renewalRequestId);
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showToast(<>{message ?? 'Failed to record the decision.'}</>, 'error');
    } finally {
      setBusyItemId(null);
    }
  }, [rejecting, rejectReason, applyDecisionResult, showToast]);

  const handleView = useCallback(async (entry: AmlSummaryEntryDTO): Promise<void> => {
    if (!entry.clientDocumentId) return;
    setViewState({ title: `${entry.requirementLabel} · ${entry.uploadedDocumentTypeLabel ?? 'Document'}` });
    setViewLoading(true);
    setViewUrl(null);
    setViewContentType(undefined);
    try {
      const { blob } = await clientDocumentApi.download(entry.clientDocumentId);
      setViewContentType(blob.type || undefined);
      setViewUrl(URL.createObjectURL(blob));
    } catch {
      // Keep url null — the modal renders "Document not available".
    } finally {
      setViewLoading(false);
    }
  }, []);

  const handleCloseViewer = useCallback((): void => {
    if (viewUrl) URL.revokeObjectURL(viewUrl);
    setViewUrl(null);
    setViewContentType(undefined);
    setViewState(null);
  }, [viewUrl]);

  const handleDownload = useCallback(
    async (entry: AmlSummaryEntryDTO): Promise<void> => {
      if (!entry.clientDocumentId) return;
      try {
        const { blob, filename } = await clientDocumentApi.download(entry.clientDocumentId);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `${entry.requirementType}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch {
        showToast(<>Failed to download the document.</>, 'error');
      }
    },
    [showToast],
  );

  const columns = useMemo<LOATableColumn<AmlSummaryEntryDTO>[]>(
    () => [
      {
        key: 'requirement',
        label: 'Requirement',
        render: (e) => <span className="font-sans text-body-03 text-black">{e.requirementLabel}</span>,
      },
      {
        key: 'provided',
        label: 'Provided document',
        render: (e) => (
          <span className="font-sans text-body-03 text-secondary-text">
            {e.uploadedDocumentTypeLabel ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (e) => <RequestDisplayStatusBadge status={ITEM_STATUS_TO_DISPLAY[e.status]} />,
      },
      {
        key: 'decided',
        label: 'Decided',
        render: (e) => (
          <span className="font-sans text-body-03 text-secondary-text">
            {e.reviewedAt ? new Date(e.reviewedAt).toLocaleDateString() : '—'}
          </span>
        ),
      },
      {
        key: 'validUntil',
        label: 'Valid until',
        render: (e) => (
          <span className="font-sans text-body-03 text-secondary-text">
            {e.validUntil ? new Date(e.validUntil).toLocaleDateString() : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  const actions = useMemo<LOATableAction<AmlSummaryEntryDTO>[]>(
    () => [
      {
        key: 'view',
        icon: <EyeIcon />,
        label: 'View document',
        onClick: (e) => {
          void handleView(e);
        },
        visible: (e) => !!e.clientDocumentId,
      },
      {
        key: 'download',
        icon: <DownloadIcon />,
        label: 'Download document',
        onClick: (e) => {
          void handleDownload(e);
        },
        visible: (e) => !!e.clientDocumentId,
      },
      {
        key: 'approve',
        icon: <AppIcon name="check circle" size={18} />,
        label: 'Approve document',
        onClick: (e) => {
          void handleApprove(e);
        },
        visible: (e) => e.status === 'UPLOADED',
        disabled: (e) => busyItemId === e.itemId,
      },
      {
        key: 'decline',
        icon: <BanIcon />,
        label: 'Decline document',
        onClick: (e) => {
          setRejecting(e);
          setRejectReason('');
        },
        visible: (e) => e.status === 'UPLOADED',
        disabled: (e) => busyItemId === e.itemId,
      },
    ],
    [busyItemId, handleApprove, handleDownload, handleView],
  );

  if (loading) {
    return (
      <div data-testid="client-aml-loading" className="flex justify-center py-2xl">
        <Spinner />
      </div>
    );
  }

  if (featureDisabled) {
    return (
      <div data-testid="client-aml-disabled">
        <Alert variant="info">
          AML documentation requests are not enabled for this brokerage.
        </Alert>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div data-testid="client-aml-error">
        <Alert variant="error">
          {error ?? 'The AML documentation could not be loaded.'}
        </Alert>
      </div>
    );
  }

  return (
    <div data-testid="client-aml-page">
      <LOATableCard
        title={`${clientName} — AML Documentation`}
        data={summary}
        columns={columns}
        actions={actions}
        keyExtractor={(e) => e.itemId}
        emptyMessage="No AML documents have been requested from this client yet."
        rowTestId={(e) => `client-aml-summary-${e.requirementType}`}
        headerAction={
          <Button
            variant="filled"
            colour="purple"
            size="md"
            onClick={() => setModalOpen(true)}
            data-testid="client-aml-request-button"
          >
            Request Documents
          </Button>
        }
      />

      <ViewLoAModal
        open={!!viewState}
        onClose={handleCloseViewer}
        documentUrl={viewUrl}
        title={viewState?.title || ''}
        loading={viewLoading}
        contentType={viewContentType}
      />

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        size="medium"
        variant="warning"
      >
        <Modal.Header
          icon="rejected"
          title={`Decline ${rejecting?.requirementLabel ?? 'document'}`}
          onClose={() => setRejecting(null)}
        />
        <Modal.Content>
          <p className="m-0 mb-sm font-sans text-body-03 text-black">
            The client will be asked to upload this document again with your reason.
          </p>
          <TextArea
            fluid
            required
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            maxLength={500}
            data-testid="aml-reject-reason"
          />
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setRejecting(null) }}
          primaryAction={{
            label: 'Decline',
            colour: 'red',
            onClick: () => void handleRejectConfirm(),
            disabled: !rejectReason.trim() || busyItemId !== null,
          }}
        />
      </Modal>

      <RequestAmlDocumentsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        profileId={client.profileId}
        targetClientId={client.id}
        clientName={clientName}
        onCreated={() => void refresh()}
      />
    </div>
  );
}

export default ClientAmlPage;
